import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, BookOpen, Plus, Search, AlertTriangle, Save, Trash2, HelpCircle, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner, EmptyState, Input, Select } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// KbAdminScreen — /admin/kb  (T-447 phase 2)
// The Ask Coretel guide library, editable in the app.
//
// WHY: the guides ARE the assistant's brain, and until now only Claude could
// change them — every fix needed a migration and a deploy. That makes the
// company depend on one person. A director can now write or correct a guide in
// under a minute, which is what keeps the library current when James is away.
//
// No migration needed: kb_articles' write policy (mig 267) is already
// director-only, so the database is the gate here as everywhere else.
// =============================================================================

const HUBS = ["general", "projects", "finance", "hr", "hse", "admin"];

type Article = {
  kb_article_id?: string;
  slug: string; title: string; hub: string | null;
  min_role: string; status: string;
  keywords: string | null; body_md: string;
  updated_at?: string;
};

const BLANK: Article = {
  slug: "", title: "", hub: "general", min_role: "manager",
  status: "published", keywords: "", body_md: "",
};

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}

export default function KbAdminScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [hub, setHub] = useState("all");
  const [editing, setEditing] = useState<Article | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const canEdit = hasRole(user, "director");

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["kb_admin"],
    enabled: !!canEdit,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kb_articles")
        .select("kb_article_id, slug, title, hub, min_role, status, keywords, body_md, updated_at")
        .order("hub", { ascending: true })
        .order("title", { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Article[];
    },
  });

  // What people asked that the assistant couldn't answer — the to-write list.
  const { data: gaps = [] } = useQuery({
    queryKey: ["kb_admin_gaps"],
    enabled: !!canEdit,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assistant_messages")
        .select("message_id, thread_id, role, content, outcome, created_at")
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      const asc = [...(data ?? [])].sort((a: any, b: any) => (a.created_at < b.created_at ? -1 : 1));
      const out: string[] = [];
      let lastQ: any = null;
      for (const m of asc as any[]) {
        if (m.role === "user") lastQ = m;
        else if (m.role === "assistant") {
          if (m.outcome === "no_result" && lastQ?.content) out.push(String(lastQ.content));
          lastQ = null;
        }
      }
      return Array.from(new Set(out.reverse())).slice(0, 12);
    },
  });

  const save = useMutation({
    mutationFn: async (a: Article) => {
      const payload = {
        slug: a.slug.trim(), title: a.title.trim(), hub: a.hub || null,
        min_role: a.min_role, status: a.status,
        keywords: (a.keywords ?? "").trim() || null, body_md: a.body_md,
      };
      if (!payload.slug || !payload.title || !payload.body_md.trim()) {
        throw new Error("Title, slug and the guide itself are all needed.");
      }
      if (a.kb_article_id) {
        const { error } = await supabase.from("kb_articles").update(payload).eq("kb_article_id", a.kb_article_id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("kb_articles").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => { setEditing(null); setErr(null); qc.invalidateQueries({ queryKey: ["kb_admin"] }); },
    onError: (e: any) => setErr(e?.message ?? "Could not save that."),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("kb_articles").delete().eq("kb_article_id", id);
      if (error) throw error;
    },
    onSuccess: () => { setEditing(null); qc.invalidateQueries({ queryKey: ["kb_admin"] }); },
    onError: (e: any) => setErr(e?.message ?? "Could not delete that."),
  });

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return articles.filter(a => {
      if (hub !== "all" && (a.hub ?? "general") !== hub) return false;
      if (!needle) return true;
      return `${a.title} ${a.slug} ${a.keywords ?? ""}`.toLowerCase().includes(needle);
    });
  }, [articles, q, hub]);

  if (!user) return null;
  if (!canEdit) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><EmptyState icon={AlertTriangle} title="Directors only"
          description="The guide library is edited by directors. Anyone manager and above can read the guides through Ask Coretel." /></Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto pb-12">
      <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Home
      </button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-text flex items-center gap-2">
            <BookOpen size={20} className="text-accent" /> Ask Coretel guides
          </h1>
          <p className="text-[13px] text-text-muted mt-1">
            {articles.length} guides. These are what the assistant answers from — fix one here and it's live immediately, no deploy.
          </p>
        </div>
        <Button icon={Plus} onClick={() => { setErr(null); setEditing({ ...BLANK }); }}>New guide</Button>
      </div>

      {gaps.length > 0 && (
        <Card className="mb-5 border-warn/40">
          <div className="p-4">
            <div className="text-[13px] font-medium text-warn flex items-center gap-2">
              <HelpCircle size={15} /> Asked, but we had no answer
            </div>
            <p className="text-[11px] text-text-muted mt-1 mb-2">
              Questions the assistant couldn't answer. Each one is a guide worth writing — click to start it.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {gaps.map((g, i) => (
                <button key={i} type="button"
                  onClick={() => { setErr(null); setEditing({ ...BLANK, title: g.slice(0, 90), slug: slugify(g.slice(0, 50)) }); }}
                  className="text-[11px] px-2 py-1 rounded border border-line hover:bg-ink-hover text-text text-left">
                  {g.length > 90 ? g.slice(0, 90) + "…" : g}
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, slug or keywords…" className="pl-9" />
        </div>
        <div className="w-[170px]">
          <Select value={hub} onChange={e => setHub(e.target.value)}>
            <option value="all">All hubs</option>
            {HUBS.map(h => <option key={h} value={h}>{h}</option>)}
          </Select>
        </div>
      </div>

      <Card>
        <div className="p-5">
          {isLoading ? (
            <div className="py-10 flex justify-center"><Spinner /></div>
          ) : shown.length === 0 ? (
            <EmptyState icon={BookOpen} title="Nothing matches"
              description="No guide matches that search. Try different words, or write a new one." />
          ) : (
            <div className="divide-y divide-line/50">
              {shown.map(a => (
                <button key={a.kb_article_id} type="button"
                  onClick={() => { setErr(null); setEditing({ ...a }); }}
                  className="w-full text-left py-2.5 hover:bg-ink-hover px-1 rounded">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] text-text flex-1 min-w-[200px]">{a.title}</span>
                    <span className="text-[10px] text-text-dim font-mono">{a.hub ?? "general"}</span>
                    {a.status !== "published" && <span className="text-[10px] text-warn">{a.status}</span>}
                    {a.min_role === "director" && <span className="text-[10px] text-info">director only</span>}
                    <span className="text-[10px] text-text-dim">{a.updated_at ? fmtDate(a.updated_at) : ""}</span>
                  </div>
                  <div className="text-[11px] text-text-dim font-mono mt-0.5">{a.slug}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-ink-card border border-line rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-line flex items-start justify-between gap-3 sticky top-0 bg-ink-card">
              <div>
                <h3 className="text-[15px] font-semibold">{editing.kb_article_id ? "Edit guide" : "New guide"}</h3>
                <p className="text-[11px] text-text-muted mt-0.5">
                  Write it the way you'd tell someone standing next to you. Say where to go, then the steps.
                </p>
              </div>
              <button onClick={() => setEditing(null)} className="text-text-muted hover:text-text p-1" type="button"><X size={18} /></button>
            </div>

            <div className="p-5 space-y-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">Title — how someone would say it</span>
                <Input value={editing.title} placeholder="e.g. Record a pay run"
                  onChange={e => setEditing({ ...editing, title: e.target.value,
                    slug: editing.kb_article_id ? editing.slug : slugify(e.target.value) })} />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-text-muted">Slug</span>
                  <Input value={editing.slug} disabled={!!editing.kb_article_id}
                    onChange={e => setEditing({ ...editing, slug: slugify(e.target.value) })} />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-text-muted">Hub</span>
                  <Select value={editing.hub ?? "general"} onChange={e => setEditing({ ...editing, hub: e.target.value })}>
                    {HUBS.map(h => <option key={h} value={h}>{h}</option>)}
                  </Select>
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wide text-text-muted">Who can see it</span>
                  <Select value={editing.min_role} onChange={e => setEditing({ ...editing, min_role: e.target.value })}>
                    <option value="manager">Managers + directors</option>
                    <option value="director">Directors only</option>
                  </Select>
                </label>
              </div>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">
                  Other words people might use
                </span>
                <Input value={editing.keywords ?? ""} placeholder="mirror, copy someone's week, duplicate roster, same hours"
                  onChange={e => setEditing({ ...editing, keywords: e.target.value })} />
                <span className="block text-[10px] text-text-dim mt-1">
                  This is how search finds it. Put the words YOUR team would use, not the app's wording — that's why
                  "copy someone's hours" used to miss "Fill gaps".
                </span>
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">The guide</span>
                <textarea value={editing.body_md} rows={14}
                  onChange={e => setEditing({ ...editing, body_md: e.target.value })}
                  placeholder={"What it's for, in a line.\n\nWhere: HR > Payroll (/hr/payroll). Needs director or payroll access.\n\nSteps:\n1. ...\n2. ...\n\nTip: the trap people fall into."}
                  className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink text-text text-[13px] font-mono resize-y focus:outline-none focus:border-accent" />
                <span className="block text-[10px] text-text-dim mt-1">
                  Include the menu path AND the /route, and say if it's role-gated — the assistant repeats that, and
                  "it exists but only directors see it" is a far better answer than "I can't find it".
                </span>
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-text-muted">Status</span>
                <Select value={editing.status} onChange={e => setEditing({ ...editing, status: e.target.value })}>
                  <option value="published">Published — the assistant uses it</option>
                  <option value="draft">Draft — only directors see it</option>
                </Select>
              </label>

              {err && <div className="text-[12px] text-danger">{err}</div>}
            </div>

            <div className="p-4 border-t border-line bg-ink-raised flex items-center justify-between gap-2 sticky bottom-0">
              <div>
                {editing.kb_article_id && (
                  <button type="button" disabled={remove.isPending}
                    onClick={() => { if (window.confirm(`Delete "${editing.title}"? The assistant will stop using it.`)) remove.mutate(editing.kb_article_id!); }}
                    className="text-[12px] text-text-muted hover:text-danger inline-flex items-center gap-1.5">
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(null)} type="button" className="px-3 py-2 text-[13px] text-text-muted hover:text-text">Cancel</button>
                <Button icon={Save} disabled={save.isPending} onClick={() => save.mutate(editing)}>
                  {save.isPending ? "Saving…" : "Save — live straight away"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
